import { TTokenRedemptionOptionSelectResponse } from '@daymade/api-contracts';
import {
  TokenRedemptionOptionGroup,
  TokenRedemptionOptionType,
} from '@daymade/common-lib/dbEnums';
import LoadingSpinner from 'components/LoadingSpinner';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { useEffect, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import TokenTownDrawPage from './slugPages/draw';
import PrizeRedemptionPage from './slugPages/prize';
import WeeklyDrawEntryPage from './slugPages/weeklyDraw';
import TreeRedemptionPage from './tree';

export enum PageType {
  DRAW = 'DRAW',
  PRIZE = 'PRIZE',
  TREE = 'TREE',
  WEEKLY_DRAW = 'WEEKLY_DRAW',
}

interface ITokenTownSubPage {
  redemptionOption: TTokenRedemptionOptionSelectResponse | null;
}

const TokenTownSubPage = observer(({ redemptionOption }: ITokenTownSubPage) => {
  const [loading, setLoading] = useState(true);
  const [pageType, setPageType] = useState<PageType | null>(null);

  useEffect(() => {
    if (redemptionOption?.type === TokenRedemptionOptionType.ENTRY) {
      if (redemptionOption.group === TokenRedemptionOptionGroup.PRIZE) {
        setPageType(PageType.WEEKLY_DRAW);
      } else {
        setPageType(PageType.DRAW);
      }
    } else if (redemptionOption?.type === TokenRedemptionOptionType.PRIZE) {
      setPageType(PageType.PRIZE);
    } else if (redemptionOption?.type === TokenRedemptionOptionType.TREE) {
      setPageType(PageType.TREE);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redemptionOption]);

  if (loading) {
    return <LoadingSpinner />;
  }

  switch (pageType) {
    case PageType.DRAW:
      return <TokenTownDrawPage />;
    case PageType.PRIZE:
      return <PrizeRedemptionPage />;
    case PageType.WEEKLY_DRAW:
      return <WeeklyDrawEntryPage weeklyDrawEntryDetails={redemptionOption} />;
    case PageType.TREE:
      return <TreeRedemptionPage treeRedemptionDetails={redemptionOption} />;
  }
});

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const slug = Array.isArray(ctx?.params?.slug)
    ? ctx?.params?.slug[0]
    : ctx?.params?.slug;

  let redemptionOption = null;

  const redirectToDestination = (destination = '/token-town') => {
    return {
      redirect: {
        destination,
        statusCode: 302,
      } as const,
    };
  };

  if (!slug) {
    return redirectToDestination();
  }

  if (slug === 'draw') {
    const id = Array.isArray(ctx?.params?.slug) ? ctx?.params?.slug[1] : null;

    if (!id) {
      return redirectToDestination();
    }

    const resp = await tokenApiClient.public.getTokenRedemptionOption({
      params: { id: Number(id) },
    });

    switch (resp.status) {
      case HttpStatusCode.OK:
        let destination = '/token-town';

        if (resp.body.drawDetails?.group !== 'LEGACY') {
          destination = `/token-town/${resp.body.slug}`;
        }

        return redirectToDestination(destination);
      case HttpStatusCode.NOT_FOUND:
        console.error(
          `Error fetching token redemption details: ${resp.body.message}`,
        );
        return redirectToDestination();
    }
  } else {
    const resp = await tokenApiClient.public.getTokenRedemptionOptionBySlug({
      params: { slug },
    });

    switch (resp.status) {
      case HttpStatusCode.OK:
        redemptionOption = {
          ...resp.body,
        };
        break;
      case HttpStatusCode.NOT_FOUND:
        console.error(
          `Error fetching token redemption details: ${resp.body.message}`,
        );
        return redirectToDestination();
    }
  }

  return {
    props: {
      redemptionOption: JSON.parse(JSON.stringify(redemptionOption)),
    },
  };
};

export default TokenTownSubPage;
